/**
 * Booking Widget Component
 * Main component for the self-service booking flow
 * Can be embedded in an iframe on external sites
 */

import { useState, useEffect, useCallback, useRef, type RefObject } from 'react';
import { Calendar, Clock, Pencil } from 'lucide-react';
import { ServiceTypeSelector } from './ServiceTypeSelector';
import { DateTimePicker } from './DateTimePicker';
import { CustomerForm } from './CustomerForm';
import { BookingConfirmation } from './BookingConfirmation';
import {
  getServiceTypes,
  getAvailability,
  createBooking,
} from '../../services/publicBookingApi';

/**
 * Hook to send postMessage for iframe resize
 */
function useIframeResize(containerRef: RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    // Only run if we're in an iframe
    if (window.parent === window) return;

    const sendHeight = () => {
      const height = containerRef.current?.scrollHeight || document.body.scrollHeight;
      window.parent.postMessage(
        { type: 'LEAL_BOOKING_RESIZE', height },
        '*'
      );
    };

    // Send initial height
    sendHeight();

    // Observe DOM changes for resize
    const observer = new ResizeObserver(() => {
      sendHeight();
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    // Also send on window resize
    window.addEventListener('resize', sendHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', sendHeight);
    };
  }, [containerRef]);
}

type Step = 'service' | 'datetime' | 'contact' | 'vehicle' | 'confirmation';

interface ServiceType {
  id: string;
  name: string;
  nameEn: string | null;
  description: string | null;
  estimatedDurationMins: number;
}

interface TimeSlot {
  date: string;
  time: string;
  available: boolean;
  serviceTypes: Array<{
    id: string;
    availableCount: number;
  }>;
}

interface CustomerFormData {
  caseCategory: 'particular' | 'seguro' | 'garantia';
  insuranceCompany?: string;
  insurancePolicyNumber?: string;
  insuranceClaimNumber?: string;
  insuranceAdjusterName?: string;
  insuranceAdjusterPhone?: string;
  insuranceAdjusterEmail?: string;
  name: string;
  email: string;
  phone: string;
  countryCode: import('@leal-crm/shared/utils/phoneUtils').CountryCode;
  dni?: string;
  vehicleRegistration: string;
  vehicleMake: string;
  vehicleModel: string;
  vehicleYear?: number;
  notes?: string;
  gdprConsent: boolean;
  marketingConsent: boolean;
}

interface BookingConfirmationData {
  confirmationNumber?: string;
  trackingToken: string;
  scheduledDate: string;
  scheduledTime: string;
}

interface CustomStyles {
  primaryColor?: string;
  backgroundColor?: string;
  textColor?: string;
  borderRadius?: number;
  buttonText?: string;
}

interface BookingWidgetProps {
  language?: 'es' | 'en';
  source?: 'public_widget' | 'marketing_link';
  campaignId?: string;
  onComplete?: (data: BookingConfirmationData) => void;
  embedded?: boolean;
  customStyles?: CustomStyles;
  hideHeader?: boolean;
  showProgress?: boolean;
  headerText?: string;
  initialServiceTypeId?: string;
  variant?: 'flat' | 'card';
}

export function BookingWidget({
  language = 'es',
  source = 'public_widget',
  campaignId,
  onComplete,
  embedded: _embedded = false,
  customStyles: _customStyles,
  hideHeader = false,
  showProgress = true,
  headerText,
  initialServiceTypeId,
  variant = 'flat',
}: BookingWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState<Step>(initialServiceTypeId ? 'datetime' : 'service');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Enable iframe resize communication
  useIframeResize(containerRef);

  const resolvedStyles = {
    primaryColor: _customStyles?.primaryColor ?? 'var(--embed-primary, #2563eb)',
    backgroundColor: _customStyles?.backgroundColor ?? 'var(--embed-bg, #ffffff)',
    textColor: _customStyles?.textColor ?? 'var(--embed-text, #1f2937)',
    borderRadius: _customStyles?.borderRadius,
    buttonText: _customStyles?.buttonText,
  };

  // Service types
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [selectedServiceTypeId, setSelectedServiceTypeId] = useState<string | null>(
    initialServiceTypeId ?? null
  );

  // Apply initial service selection once service types load (avoids selecting an invalid id)
  useEffect(() => {
    if (!initialServiceTypeId) return;
    if (selectedServiceTypeId) return;
    if (serviceTypes.length === 0) return;

    const exists = serviceTypes.some((t) => t.id === initialServiceTypeId);
    if (exists) {
      setSelectedServiceTypeId(initialServiceTypeId);
      setStep('datetime');
    }
  }, [initialServiceTypeId, selectedServiceTypeId, serviceTypes]);

  // Date/time
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);

  // Customer form
  const [formData, setFormData] = useState<CustomerFormData>({
    caseCategory: 'particular',
    insuranceCompany: '',
    insurancePolicyNumber: '',
    insuranceClaimNumber: '',
    insuranceAdjusterName: '',
    insuranceAdjusterPhone: '',
    insuranceAdjusterEmail: '',
    name: '',
    email: '',
    phone: '',
    countryCode: 'ES',
    dni: '',
    vehicleRegistration: '',
    vehicleMake: '',
    vehicleModel: '',
    gdprConsent: false,
    marketingConsent: false,
  });
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof CustomerFormData, string>>>({});

  // Confirmation
  const [confirmation, setConfirmation] = useState<BookingConfirmationData | null>(null);

  // Load service types on mount
  useEffect(() => {
    async function loadServiceTypes() {
      try {
        const types = await getServiceTypes();
        setServiceTypes(types);
      } catch (err) {
        setError(language === 'es' ? 'Error cargando servicios' : 'Error loading services');
      }
    }
    loadServiceTypes();
  }, [language]);

  // Load availability when service type is selected
  useEffect(() => {
    async function loadAvailability() {
      if (!selectedServiceTypeId) return;

      setLoading(true);
      try {
        const today = new Date();
        const endDate = new Date();
        endDate.setDate(endDate.getDate() + 14);

        const startDateStr = today.toISOString().split('T')[0];
        const endDateStr = endDate.toISOString().split('T')[0];

        const availableSlots = await getAvailability(startDateStr, endDateStr, selectedServiceTypeId);
        setSlots(availableSlots);
        setError(null);
      } catch (err) {
        setError(
          language === 'es' ? 'Error cargando disponibilidad' : 'Error loading availability'
        );
      } finally {
        setLoading(false);
      }
    }
    loadAvailability();
  }, [selectedServiceTypeId, language]);

  // Handle service type selection
  const handleServiceSelect = useCallback((id: string) => {
    setSelectedServiceTypeId(id);
    setSelectedDate(null);
    setSelectedTime(null);
    setStep('datetime');
  }, []);

  // Handle date/time selection
  const handleDateTimeSelect = useCallback((date: string, time: string) => {
    setSelectedDate(date);
    if (time) {
      setSelectedTime(time);
      setStep('contact');
    }
  }, []);

  // Validate contact stage fields
  const validateContactStage = useCallback((): boolean => {
    const errors: Partial<Record<keyof CustomerFormData, string>> = {};
    const t = language === 'es' ? validationMessages.es : validationMessages.en;

    // Validate case category and insurance fields
    if (formData.caseCategory === 'seguro') {
      if (!formData.insuranceCompany?.trim()) errors.insuranceCompany = t.required;
      if (!formData.insuranceClaimNumber?.trim()) errors.insuranceClaimNumber = t.required;

      if (
        formData.insuranceAdjusterEmail?.trim() &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.insuranceAdjusterEmail)
      ) {
        errors.insuranceAdjusterEmail = t.invalidEmail;
      }
    }

    // Validate contact fields
    if (!formData.name.trim()) errors.name = t.required;
    if (!formData.email.trim()) {
      errors.email = t.required;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = t.invalidEmail;
    }
    if (!formData.phone.trim()) {
      errors.phone = t.required;
    } else if (formData.phone.replace(/\s/g, '').length < 9) {
      errors.phone = t.invalidPhone;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, language]);

  // Validate vehicle stage fields
  const validateVehicleStage = useCallback((): boolean => {
    const errors: Partial<Record<keyof CustomerFormData, string>> = {};
    const t = language === 'es' ? validationMessages.es : validationMessages.en;

    if (!formData.vehicleRegistration.trim()) errors.vehicleRegistration = t.required;
    if (!formData.vehicleMake.trim()) errors.vehicleMake = t.required;
    if (!formData.vehicleModel.trim()) errors.vehicleModel = t.required;
    if (!formData.gdprConsent) errors.gdprConsent = t.gdprRequired;

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, language]);

  // Validate full form (both stages)
  const validateForm = useCallback((): boolean => {
    const contactValid = validateContactStage();
    const vehicleValid = validateVehicleStage();
    return contactValid && vehicleValid;
  }, [validateContactStage, validateVehicleStage]);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!validateVehicleStage()) return;
    if (!selectedServiceTypeId || !selectedDate || !selectedTime) return;

    setLoading(true);
    setError(null);

    try {
      // Convert date and time to Unix timestamp
      const dateTime = new Date(`${selectedDate}T${selectedTime}:00`);
      const scheduledAt = Math.floor(dateTime.getTime() / 1000);

      const result = await createBooking({
        serviceTypeId: selectedServiceTypeId,
        scheduledAt,
        caseCategory: formData.caseCategory,
        insurance:
          formData.caseCategory === 'seguro'
            ? {
                company: formData.insuranceCompany || '',
                policyNumber: formData.insurancePolicyNumber || undefined,
                claimNumber: formData.insuranceClaimNumber || undefined,
                adjusterName: formData.insuranceAdjusterName?.trim() || undefined,
                adjusterPhone: formData.insuranceAdjusterPhone?.trim() || undefined,
                adjusterEmail: formData.insuranceAdjusterEmail?.trim() || undefined,
              }
            : undefined,
        customer: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
        },
        vehicle: {
          registration: formData.vehicleRegistration,
          make: formData.vehicleMake,
          model: formData.vehicleModel,
          year: formData.vehicleYear,
        },
        notes: formData.notes,
        gdprConsent: true,
        marketingConsent: formData.marketingConsent,
        source,
        campaignId,
      });

      if (result.success) {
        const confirmationData: BookingConfirmationData = {
          confirmationNumber: result.data.confirmationNumber,
          trackingToken: result.data.trackingToken,
          scheduledDate: selectedDate,
          scheduledTime: selectedTime,
        };
        setConfirmation(confirmationData);
        setStep('confirmation');
        onComplete?.(confirmationData);

        // Send postMessage for iframe integration
        if (window.parent !== window) {
          window.parent.postMessage(
            { type: 'LEAL_BOOKING_COMPLETE', data: confirmationData },
            '*'
          );
        }
      } else {
        if ('code' in result.error && result.error.code === 'SLOT_UNAVAILABLE') {
          setError(
            language === 'es'
              ? 'El horario seleccionado ya no está disponible. Por favor, elige otro.'
              : 'The selected time is no longer available. Please choose another.'
          );
          // Reload availability
          setStep('datetime');
        } else {
          setError(result.error.message);
        }
      }
    } catch (err) {
      setError(language === 'es' ? 'Error al crear la cita' : 'Error creating booking');
    } finally {
      setLoading(false);
    }
  }, [
    validateForm,
    selectedServiceTypeId,
    selectedDate,
    selectedTime,
    formData,
    source,
    campaignId,
    language,
    onComplete,
  ]);

  // Navigation
  const goBack = useCallback(() => {
    if (step === 'datetime') setStep('service');
    else if (step === 'contact') setStep('datetime');
    else if (step === 'vehicle') setStep('contact');
  }, [step]);

  const getStepNumber = () => {
    switch (step) {
      case 'service':
        return 1;
      case 'datetime':
        return 2;
      case 'contact':
        return 3;
      case 'vehicle':
        return 4;
      case 'confirmation':
        return 5;
      default:
        return 1;
    }
  };

  return (
    (() => {
      const embeddedCard = _embedded && variant === 'card';
      const wrapperClasses = `max-w-md mx-auto overflow-hidden flex flex-col ${
        _embedded && !embeddedCard ? 'max-h-screen' : 'shadow-lg rounded-lg'
      }`;

      return (
    <div
      ref={containerRef}
      className={wrapperClasses}
      style={{
        backgroundColor: _embedded && !embeddedCard ? 'transparent' : resolvedStyles.backgroundColor,
        color: resolvedStyles.textColor,
        borderRadius:
          _embedded && !embeddedCard
            ? undefined
            : resolvedStyles.borderRadius
              ? `${resolvedStyles.borderRadius}px`
              : undefined,
      }}
    >
      {/* Header */}
      {!hideHeader && (
        <div
          className={`${_embedded ? 'px-4 py-2' : 'px-6 py-4'}`}
          style={{
            backgroundColor: resolvedStyles.primaryColor,
          }}
        >
          <h2 className={`text-white font-semibold ${_embedded ? 'text-base' : 'text-lg'}`}>
            {headerText || (language === 'es' ? 'Reservar cita' : 'Book appointment')}
          </h2>
          {showProgress && step !== 'confirmation' && (
            <div className={`flex gap-2 ${_embedded ? 'mt-2' : 'mt-3'}`}>
              {[1, 2, 3, 4].map((num) => (
                <div
                  key={num}
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    num <= getStepNumber() ? 'bg-white' : 'bg-white/40'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div className={`${_embedded ? 'p-3 flex-1 overflow-y-auto' : 'p-6'}`}>
        {/* Error message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && step !== 'confirmation' && (
          <div className="flex justify-center py-8">
            <div
              className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: resolvedStyles.primaryColor, borderTopColor: 'transparent' }}
            />
          </div>
        )}

        {/* Steps */}
        {!loading && step === 'service' && (
          <ServiceTypeSelector
            serviceTypes={serviceTypes}
            selectedId={selectedServiceTypeId}
            onSelect={handleServiceSelect}
            language={language}
            primaryColor={resolvedStyles.primaryColor}
          />
        )}

        {!loading && step === 'datetime' && selectedServiceTypeId && (
          <DateTimePicker
            slots={slots}
            selectedServiceTypeId={selectedServiceTypeId}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            onSelect={handleDateTimeSelect}
            language={language}
            primaryColor={resolvedStyles.primaryColor}
          />
        )}

        {!loading && step === 'contact' && (
          <CustomerForm
            data={formData}
            onChange={setFormData}
            errors={formErrors}
            language={language}
            stage="contact"
          />
        )}

        {!loading && step === 'vehicle' && (
          <>
            {/* Compact service summary bar */}
            {selectedServiceTypeId && selectedDate && selectedTime && (
              <div className="mb-3 p-2 rounded-md border" style={{ backgroundColor: `${resolvedStyles.primaryColor}10`, borderColor: `${resolvedStyles.primaryColor}30` }}>
                <div className="flex items-center justify-between gap-1.5">
                  <div className="flex flex-wrap items-center gap-1.5 text-xs">
                    {/* Service chip */}
                    <span
                      className="px-2 py-0.5 text-white text-[10px] font-medium rounded-full"
                      style={{ backgroundColor: resolvedStyles.primaryColor }}
                    >
                      {language === 'en'
                        ? serviceTypes.find((t) => t.id === selectedServiceTypeId)?.nameEn ||
                          serviceTypes.find((t) => t.id === selectedServiceTypeId)?.name
                        : serviceTypes.find((t) => t.id === selectedServiceTypeId)?.name}
                    </span>
                    {/* Date */}
                    <span className="flex items-center gap-0.5 text-gray-600">
                      <Calendar className="h-3 w-3" />
                      {new Date(selectedDate + 'T00:00:00').toLocaleDateString(
                        language === 'es' ? 'es-ES' : 'en-US',
                        { weekday: 'short', month: 'short', day: 'numeric' }
                      )}
                    </span>
                    {/* Time */}
                    <span className="flex items-center gap-0.5 text-gray-600">
                      <Clock className="h-3 w-3" />
                      {selectedTime}
                    </span>
                  </div>
                  {/* Change button */}
                  <button
                    type="button"
                    onClick={() => setStep('service')}
                    className="flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded transition-colors"
                    style={{ color: resolvedStyles.primaryColor }}
                  >
                    <Pencil className="h-2.5 w-2.5" />
                    {language === 'es' ? 'Cambiar' : 'Change'}
                  </button>
                </div>
              </div>
            )}
            <CustomerForm
              data={formData}
              onChange={setFormData}
              errors={formErrors}
              language={language}
              stage="vehicle"
            />
          </>
        )}

        {step === 'confirmation' && confirmation && (
          <BookingConfirmation
            confirmationNumber={confirmation.confirmationNumber}
            scheduledDate={confirmation.scheduledDate}
            scheduledTime={confirmation.scheduledTime}
            trackingToken={confirmation.trackingToken}
            language={language}
          />
        )}
      </div>

      {/* Footer with navigation */}
      {step !== 'confirmation' && (
        <div className={`${_embedded ? 'px-3 pb-3' : 'px-6 pb-6'} flex gap-2`}>
          {step !== 'service' && (
            <button
              onClick={goBack}
              disabled={loading}
              className={`flex-1 px-3 ${_embedded ? 'py-2' : 'py-2.5'} text-sm text-gray-700 bg-gray-100 rounded-md font-medium hover:bg-gray-200 disabled:opacity-50 transition-colors`}
            >
              {language === 'es' ? 'Atrás' : 'Back'}
            </button>
          )}

          {step === 'contact' && (
            <button
              onClick={() => {
                if (validateContactStage()) {
                  setStep('vehicle');
                }
              }}
              disabled={loading}
              className={`btn-primary flex-1 px-3 ${_embedded ? 'py-2' : 'py-2.5'} text-sm text-white rounded-md font-medium disabled:opacity-50 transition-colors`}
              style={{ backgroundColor: resolvedStyles.primaryColor }}
            >
              {language === 'es' ? 'Continuar' : 'Continue'}
            </button>
          )}

          {step === 'vehicle' && (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className={`btn-primary flex-1 px-3 ${_embedded ? 'py-2' : 'py-2.5'} text-sm text-white rounded-md font-medium disabled:opacity-50 transition-colors`}
              style={{ backgroundColor: resolvedStyles.primaryColor }}
            >
              {loading
                ? language === 'es'
                  ? 'Reservando...'
                  : 'Booking...'
                : resolvedStyles.buttonText
                  ? resolvedStyles.buttonText
                  : language === 'es'
                    ? 'Confirmar cita'
                    : 'Confirm booking'}
            </button>
          )}
        </div>
      )}
    </div>
  );
})()
  );
}

const validationMessages = {
  es: {
    required: 'Este campo es obligatorio',
    invalidEmail: 'Email no válido',
    invalidPhone: 'Teléfono no válido',
    gdprRequired: 'Debes aceptar la política de privacidad',
  },
  en: {
    required: 'This field is required',
    invalidEmail: 'Invalid email',
    invalidPhone: 'Invalid phone number',
    gdprRequired: 'You must accept the privacy policy',
  },
};
